import React, { useState } from 'react';
import { Layout, Menu, Button, message, Modal, Form, Input } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  DashboardOutlined,
  UserOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  EditOutlined,
  BarChartOutlined,
  LogoutOutlined,
  ApartmentOutlined,
  AuditOutlined,
  SolutionOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import { changeAdminPassword } from '../api/admin';

const { Header, Sider, Content } = Layout;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [pwdModalVisible, setPwdModalVisible] = useState(false);
  const [pwdForm] = Form.useForm();
  const [changingPwd, setChangingPwd] = useState(false);

  const handleLogout = () => {
    logout();
    message.success('已退出登录');
    navigate('/admin/login');
  };

  const handleChangePassword = async () => {
    try {
      const values = await pwdForm.validateFields();
      setChangingPwd(true);
      const res = await changeAdminPassword({
        old_password: values.old_password,
        new_password: values.new_password
      });
      if (res.code === 200) {
        message.success('密码修改成功');
        setPwdModalVisible(false);
        pwdForm.resetFields();
      } else {
        message.error(res.message || '修改失败');
      }
    } catch (error) {
      message.error(error.message || '修改失败');
    } finally {
      setChangingPwd(false);
    }
  };

  const menuItems = [
    {
      key: '/admin/dashboard',
      icon: <DashboardOutlined />,
      label: '首页概览',
    },
    {
      key: '/admin/users',
      icon: <UserOutlined />,
      label: '用户管理',
    },
    {
      key: '/admin/departments',
      icon: <ApartmentOutlined />,
      label: '部门管理',
    },
    {
      key: '/admin/contents',
      icon: <FileTextOutlined />,
      label: '内容管理',
    },
    {
      key: '/admin/questions',
      icon: <QuestionCircleOutlined />,
      label: '找茬题库',
    },
    {
      key: '/admin/fillblanks',
      icon: <EditOutlined />,
      label: '完形填空',
    },
    {
      key: '/admin/statistics',
      icon: <BarChartOutlined />,
      label: '数据统计',
    },
    {
      key: '/admin/promises',
      icon: <SolutionOutlined />,
      label: '承诺查看',
    },
    {
      key: '/admin/login-logs',
      icon: <AuditOutlined />,
      label: '登录日志',
    },
  ];

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        theme="dark" 
        width={180}
        style={{ 
          boxShadow: '2px 0 8px rgba(0,0,0,0.15)'
        }}
      >
        <div style={{ 
          height: '64px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          管理后台
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ marginTop: '10px' }}
        />
      </Sider>
      <Layout>
        <Header style={{ 
          padding: '0 24px', 
          background: '#fff', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '18px', fontWeight: '500' }}>
            学习平台
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button 
              type="link" 
              icon={<UserOutlined />}
              onClick={() => setPwdModalVisible(true)}
            >
              {user?.name || '管理员'}
            </Button>
            <Button 
              type="link" 
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              退出
            </Button>
          </div>
        </Header>
        <Content style={{ 
          margin: '40px',
          padding: '40px',
          background: '#fff',
          borderRadius: '8px',
          minHeight: 'calc(100vh - 112px)'
        }}>
          <Outlet />
        </Content>
      </Layout>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={pwdModalVisible}
        onOk={handleChangePassword}
        onCancel={() => {
          setPwdModalVisible(false);
          pwdForm.resetFields();
        }}
        confirmLoading={changingPwd}
        okText="确认修改"
        cancelText="取消"
      >
        <Form
          form={pwdForm}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="old_password"
            label="旧密码"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入旧密码" />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码不能少于6位' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入新密码（至少6位）" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AdminLayout;
